// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { DSNSnapshot, SpacecraftInfo } from '../types';
import { SPACECRAFT, CATEGORY_COLOR, CATEGORY_LABEL, getSpacecraftInfo } from '../data/spacecraft';
import { extractPairs, uniqueSpacecraft } from '../state';

let activeCategory: SpacecraftInfo['category'] | 'all' = 'all';

export function setCategory(cat: SpacecraftInfo['category'] | 'all'): void {
  activeCategory = cat;
}

export function renderSpacecraftDirectory(snapshot: DSNSnapshot | null): string {
  const liveCodes = snapshot ? new Set(uniqueSpacecraft(extractPairs(snapshot))) : new Set<string>();

  const entries = Object.values(SPACECRAFT).filter(
    (s) => s.category !== 'other' && s.code !== 'DSN' && s.code !== 'DSS' && s.code !== 'CGO',
  );

  const categories: Array<SpacecraftInfo['category']> = [
    'interstellar',
    'outer-planets',
    'asteroid',
    'l1-l2',
    'solar',
    'mars',
    'moon',
    'inner-planets',
    'earth-orbit',
  ];

  const filtered = activeCategory === 'all' ? entries : entries.filter((e) => e.category === activeCategory);

  const seen = new Set<string>();
  const uniqueEntries = filtered.filter((e) => {
    const key = e.name;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  uniqueEntries.sort((a, b) => {
    const aLive = liveCodes.has(a.code) ? 0 : 1;
    const bLive = liveCodes.has(b.code) ? 0 : 1;
    if (aLive !== bLive) return aLive - bLive;
    return a.name.localeCompare(b.name);
  });

  const filterPills = [
    `<button class="filter-pill ${activeCategory === 'all' ? 'active' : ''}" data-cat="all">All <span style="color: var(--text-tertiary); font-family: var(--font-mono); font-size: 11px;">${entries.length}</span></button>`,
    ...categories.map((cat) => {
      const count = entries.filter((e) => e.category === cat).length;
      if (count === 0) return '';
      return `<button class="filter-pill ${activeCategory === cat ? 'active' : ''}" data-cat="${cat}">
        <span class="cat-dot" style="background: ${CATEGORY_COLOR[cat]}"></span>
        ${CATEGORY_LABEL[cat]}
        <span style="color: var(--text-tertiary); font-family: var(--font-mono); font-size: 11px;">${count}</span>
      </button>`;
    }),
  ].join('');

  const cards = uniqueEntries
    .map((sc) => {
      const isLive = liveCodes.has(sc.code);
      return `
        <button class="sc-card ${isLive ? 'live' : ''}" data-spacecraft="${sc.code}">
          <div class="sc-card-head">
            <span class="cat-dot" style="width: 10px; height: 10px; border-radius: 50%; background: ${CATEGORY_COLOR[sc.category]}"></span>
            <span class="sc-code">${sc.code}</span>
            ${isLive ? '<span class="sc-live-pill">● Live</span>' : ''}
          </div>
          <div class="sc-name">${sc.name}</div>
          <div class="sc-mission">${sc.mission}</div>
          <div class="sc-desc">${sc.description}</div>
          <div class="sc-meta">
            <span>${sc.agency}</span>
            <span>·</span>
            <span>Launched ${sc.launched}</span>
          </div>
        </button>
      `;
    })
    .join('');

  return `
    <div class="view-header">
      <h2 class="view-title">Spacecraft directory</h2>
      <p class="view-subtitle">
        ${entries.length} curated missions the DSN regularly tracks, grouped by where they are in the Solar System.
        Spacecraft currently receiving a signal somewhere on the network show a <strong style="color: var(--status-good)">● Live</strong> badge.
        Click any card for mission detail.
      </p>
    </div>
    <div class="sc-filter-bar" id="sc-filter-bar">
      ${filterPills}
    </div>
    <div class="sc-grid" id="sc-grid">
      ${cards}
    </div>
  `;
}

export function renderSpacecraftDetail(code: string, snapshot: DSNSnapshot | null): string {
  const info = getSpacecraftInfo(code);
  const pairs = snapshot ? extractPairs(snapshot) : [];
  const tracking = pairs.filter((p) => p.spacecraft === code);

  let trackingBlock = '';
  if (tracking.length > 0) {
    const rows = tracking
      .map((t) => {
        const bands = t.bands.length > 0 ? t.bands.join(' / ') : '—';
        const rate = t.dataRates.length > 0 ? Math.max(...t.dataRates) : 0;
        const rateLabel = rate > 0 ? (rate >= 1_000_000 ? `${(rate / 1_000_000).toFixed(1)} Mbps` : rate >= 1_000 ? `${(rate / 1_000).toFixed(1)} kbps` : `${rate} bps`) : 'idle';
        const status = t.activeDown || t.activeUp ? '<span style="color: var(--status-good)">● active</span>' : '<span style="color: var(--text-tertiary)">○ idle</span>';
        return `
          <div style="display: grid; grid-template-columns: 80px 120px 1fr auto; gap: var(--space-md); padding: 6px 0; border-bottom: 1px solid var(--border-subtle); font-family: var(--font-mono); font-size: var(--font-size-xs);">
            <span style="color: var(--accent-primary)">${t.dish}</span>
            <span>${t.complexName}</span>
            <span style="color: var(--text-secondary)">${bands} · ${rateLabel}</span>
            <span>${status}</span>
          </div>
        `;
      })
      .join('');
    trackingBlock = `
      <div class="sc-detail-tracking">
        <h4>Currently tracked by</h4>
        ${rows}
      </div>
    `;
  } else {
    trackingBlock = `
      <div class="sc-detail-tracking">
        <h4>Currently tracked by</h4>
        <p style="color: var(--text-tertiary); font-size: var(--font-size-sm);">Not on any antenna at this moment. Missions cycle through the three complexes as Earth rotates.</p>
      </div>
    `;
  }

  return `
    <div class="sc-detail-head">
      <div>
        <div style="display: flex; align-items: center; gap: var(--space-sm); margin-bottom: 4px;">
          <span class="cat-dot" style="width: 10px; height: 10px; border-radius: 50%; background: ${CATEGORY_COLOR[info.category]}"></span>
          <span style="color: var(--text-tertiary); font-size: var(--font-size-xs); letter-spacing: 0.08em; text-transform: uppercase;">${CATEGORY_LABEL[info.category]}</span>
        </div>
        <div class="sc-detail-title">${info.name}</div>
        <div style="color: var(--text-secondary); margin-top: 4px;">${info.mission}</div>
      </div>
      <button class="sc-detail-close" id="sc-detail-close" aria-label="Close">×</button>
    </div>
    <dl class="sc-detail-kvs">
      <dt>Code</dt><dd>${info.code}</dd>
      <dt>Agency</dt><dd>${info.agency}</dd>
      <dt>Launched</dt><dd>${info.launched}</dd>
      <dt>Category</dt><dd>${CATEGORY_LABEL[info.category]}</dd>
    </dl>
    <p class="sc-detail-desc">${info.description}</p>
    ${trackingBlock}
  `;
}
